import {
  AVATAR_MAX_BYTES,
  AVATAR_MAX_DIMENSION,
  AVATAR_MIN_DIMENSION,
  isAllowedAvatarAspectRatio,
} from 'utils/validateAvatarImage';

const CLIENT_SOFT_MAX_BYTES = 20 * 1024 * 1024;
const HEIC_MIME = /image\/hei[cf]/i;
const HEIC_EXT = /\.hei[cf]$/i;

export type PrepareImageOptions = {
  /** Center-crop to nearest allowed avatar ratio and re-encode JPEG. */
  normalizeAvatar?: boolean;
  maxBytes?: number;
  /**
   * p7: фото документа (паспорт). Любой формат кроме JPEG/PNG (WEBP, GIF, BMP...) перекодируем в JPEG,
   * слишком большие снимки уменьшаем до 4000 px по длинной стороне, лимит 15 МБ как на сервере.
   */
  documentMode?: boolean;
};

export const DOCUMENT_MAX_BYTES = 15 * 1024 * 1024;
const DOCUMENT_MAX_SIDE = 4000;

const readImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Не удалось прочитать изображение'));
    };
    img.src = url;
  });

async function convertHeicToJpeg(file: File): Promise<File> {
  const mod = await import('heic2any');
  const heic2any = mod.default;
  const converted = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.92,
  });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  const baseName = file.name.replace(HEIC_EXT, '') || 'photo';
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
}

function canvasToJpegFile(
  canvas: HTMLCanvasElement,
  fileName: string,
  quality = 0.92,
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Не удалось сохранить изображение'));
          return;
        }
        const name = fileName.replace(/\.[^.]+$/, '') || 'photo';
        resolve(new File([blob], `${name}.jpg`, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      quality,
    );
  });
}

/** Center-crop to 1:1 or 3:4 (portrait preferred when tall). */
function cropToAllowedAspect(
  img: HTMLImageElement,
): { sx: number; sy: number; sw: number; sh: number; outW: number; outH: number } {
  const { naturalWidth: w, naturalHeight: h } = img;
  const ratio = w / h;
  let target = 1;
  if (ratio < 0.95) target = 3 / 4;
  else if (ratio > 1.05) target = 4 / 3;

  let sw = w;
  let sh = h;
  if (w / h > target) {
    sw = Math.round(h * target);
  } else {
    sh = Math.round(w / target);
  }
  const sx = Math.max(0, Math.floor((w - sw) / 2));
  const sy = Math.max(0, Math.floor((h - sh) / 2));

  let outW = sw;
  let outH = sh;
  const maxSide = Math.max(outW, outH);
  if (maxSide > AVATAR_MAX_DIMENSION) {
    const scale = AVATAR_MAX_DIMENSION / maxSide;
    outW = Math.round(outW * scale);
    outH = Math.round(outH * scale);
  }
  return { sx, sy, sw, sh, outW, outH };
}

/**
 * Normalize user-picked image for upload:
 * HEIC - JPEG, size guard, optional avatar aspect crop.
 * Returns File or throws Error with RU message.
 */
export async function prepareImageFile(
  file: File,
  options: PrepareImageOptions = {},
): Promise<File> {
  const maxBytes = options.maxBytes ?? CLIENT_SOFT_MAX_BYTES;
  let working = file;

  const looksHeic =
    HEIC_MIME.test(file.type) || HEIC_EXT.test(file.name) || file.type === '';

  if (HEIC_MIME.test(file.type) || HEIC_EXT.test(file.name)) {
    try {
      working = await convertHeicToJpeg(file);
    } catch {
      throw new Error(
        'Формат HEIC с iPhone не удалось преобразовать. В настройках камеры выберите "Наиболее совместимые" или сохраните фото как JPEG/PNG.',
      );
    }
  } else if (looksHeic && !file.type) {
    // Empty MIME on some iOS picks - try HEIC convert, else continue.
    try {
      working = await convertHeicToJpeg(file);
    } catch {
      /* fall through - may already be JPEG with empty type */
    }
  }

  // p7: фото документа не отбрасываем по «мягкому» лимиту - ниже оно будет сжато до 15 МБ.
  if (!options.documentMode && working.size > maxBytes) {
    const mb = Math.round(maxBytes / (1024 * 1024));
    throw new Error(`Размер файла не должен превышать ${mb} МБ`);
  }

  const mimeOk =
    working.type === 'image/jpeg' ||
    working.type === 'image/png' ||
    (!working.type && /\.(jpe?g|png)$/i.test(working.name));
  if (!mimeOk && working.type && !working.type.startsWith('image/')) {
    throw new Error('Допустимы только JPEG и PNG');
  }

  if (options.documentMode && working.size > 60 * 1024 * 1024) {
    throw new Error('Файл больше 60 МБ. Выберите другое фото.');
  }

  if (options.documentMode) {
    const isJpegOrPng = working.type === 'image/jpeg' || working.type === 'image/png';
    let img: HTMLImageElement;
    try {
      img = await readImage(working);
    } catch {
      throw new Error('Файл не похож на фото. Выберите изображение JPEG, PNG или HEIC.');
    }
    const longSide = Math.max(img.naturalWidth, img.naturalHeight);
    if (!isJpegOrPng || longSide > DOCUMENT_MAX_SIDE || working.size > DOCUMENT_MAX_BYTES) {
      const scale = longSide > DOCUMENT_MAX_SIDE ? DOCUMENT_MAX_SIDE / longSide : 1;
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Не удалось обработать изображение');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      working = await canvasToJpegFile(canvas, working.name, 0.9);
    }
    if (working.size > DOCUMENT_MAX_BYTES) {
      throw new Error('Файл больше 15 МБ даже после сжатия. Выберите другое фото.');
    }
    return working;
  }

  if (!options.normalizeAvatar) {
    if (working.size > AVATAR_MAX_BYTES) {
      throw new Error('Размер файла не должен превышать 200 МБ');
    }
    return working;
  }

  const img = await readImage(working);
  const { naturalWidth: width, naturalHeight: height } = img;

  if (width < AVATAR_MIN_DIMENSION || height < AVATAR_MIN_DIMENSION) {
    throw new Error(
      `Минимальный размер фото: ${AVATAR_MIN_DIMENSION}×${AVATAR_MIN_DIMENSION} пикселей`,
    );
  }

  if (isAllowedAvatarAspectRatio(width, height) && width <= AVATAR_MAX_DIMENSION && height <= AVATAR_MAX_DIMENSION) {
    return working;
  }

  const crop = cropToAllowedAspect(img);
  const canvas = document.createElement('canvas');
  canvas.width = crop.outW;
  canvas.height = crop.outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Не удалось обработать изображение');
  ctx.drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, crop.outW, crop.outH);
  return canvasToJpegFile(canvas, working.name);
}
